import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  ChevronDown,
  MapPin,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import QuoteFormDialog from "../../components/QuoteFormDialog";
import { CITY_PAGES, COMMON_SERVICES } from "./configs";

import NotFound from "../../seo/NotFound";
import { useLang } from "../../i18n/LanguageContext";

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      data-testid={`city-faq-${index}`}
      className="border-b border-white/10 py-6"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-6 text-left group"
      >
        <span className="font-display font-bold text-white text-lg sm:text-xl leading-tight group-hover:text-sky-400 transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-sky-400 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`grid transition-all duration-500 ease-out ${
          open ? "grid-rows-[1fr] mt-4 opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-zinc-400 leading-relaxed text-sm sm:text-base max-w-3xl">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
};

const CityPage = () => {
  const { city } = useParams();
  const { t } = useLang();
  const cfg = CITY_PAGES[city];
  const [quoteOpen, setQuoteOpen] = useState(false);


  if (!cfg) return <NotFound />;

  const openQuote = () => setQuoteOpen(true);

  return (
    <div className="bg-[#09090b] min-h-screen" data-testid={`city-page-${city}`}>
      <Navbar onQuoteClick={openQuote} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden hero-gradient">
        <div className="absolute inset-0">
          <img
            src={cfg.heroImage}
            alt={cfg.h1}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 hero-image-mask"></div>
          <div className="absolute inset-0 grain"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-8">
            <Link to="/" className="hover:text-sky-400">Home</Link>
            <span>/</span>
            <span className="text-zinc-300">Locations</span>
            <span>/</span>
            <span className="text-sky-400">{cfg.name}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-7">
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-sky-400" />
                <span className="eyebrow">
                  {cfg.name} · {cfg.region}
                </span>
              </div>
              <h1
                data-testid="city-h1"
                className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tighter"
              >
                {cfg.h1}
              </h1>
              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-3xl">
                {cfg.intro}
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <button
                  data-testid="city-cta-quote"
                  onClick={openQuote}
                  className="btn-primary"
                >
                  Get Free Quote in {cfg.name}
                  <ArrowRight size={16} />
                </button>
                <a
                  href="tel:+15105079318"
                  className="btn-secondary"
                  data-testid="city-cta-call"
                >
                  <Phone size={16} />
                  Call (510) 507-9318
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-6">
                {t.hero.badges.map((label) => <Trust key={label} Icon={CheckCircle2} label={label} />)}
              </div>
            </div>

            <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start">
              <div className="border border-sky-400/40 bg-zinc-950/80 backdrop-blur-xl p-8 space-y-5">
                <div className="eyebrow">Serving {cfg.name}</div>
                <div className="font-display font-bold text-white text-2xl leading-tight">
                  Local crews, premium results.
                </div>
                <a
                  href="tel:+15105079318"
                  className="block text-3xl font-display font-black text-sky-400 hover:text-sky-300 transition-colors"
                >
                  (510) 507-9318
                </a>
                <p className="text-zinc-400 text-sm">
                  {t.cta.subtitle}
                </p>
                <button
                  onClick={openQuote}
                  className="btn-primary w-full justify-center"
                  data-testid="city-side-quote"
                >
                  Request Quote
                </button>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* SERVICES IN CITY */}
      <section
        data-testid="city-services"
        className="py-24 bg-[#09090b]"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-7 space-y-5">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-sky-400"></span>
                <span className="eyebrow">Services in {cfg.name}</span>
              </div>
              <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
                Everything we install in {cfg.name}, {cfg.state === "Nevada" ? "NV" : "CA"}
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-zinc-400 text-base leading-relaxed">
                Premium outdoor structure installation, assembly, and finishing —
                with local crews who know the {cfg.name} climate, HOA rules, and
                substrate.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {COMMON_SERVICES.map((s, i) => (
              <ServiceTile
                key={s.label}
                idx={i}
                service={s}
                city={cfg.name}
                onQuoteClick={openQuote}
              />
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL POINTS */}
      <section className="py-24 bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start space-y-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-sky-400"></span>
              <span className="eyebrow">Why locals choose Xavier</span>
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
              Built for {cfg.name}'s climate, code & community.
            </h2>

          </div>
          <div className="lg:col-span-7 space-y-6">
            {cfg.localPoints.map((p, i) => (
              <div
                key={i}
                data-testid={`city-local-${i}`}
                className="flex gap-5 border-t border-white/10 pt-6"
              >
                <CheckCircle2 size={22} className="text-sky-400 shrink-0 mt-0.5" />
                <p className="text-zinc-200 text-base sm:text-lg leading-relaxed">
                  {p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEIGHBORHOODS */}
      <section
        data-testid="city-neighborhoods"
        className="py-24 bg-[#09090b]"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <div className="space-y-4 max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-sky-400"></span>
                <span className="eyebrow">Neighborhoods served</span>
              </div>
              <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
                Across {cfg.name} and nearby
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/5">
            {cfg.neighborhoods.map((n, i) => (
              <div
                key={n}
                data-testid={`hood-${i}`}
                className="bg-zinc-950 hover:bg-sky-500/5 transition-colors p-5 flex items-start gap-3"
              >
                <MapPin size={14} className="text-sky-400 mt-1 shrink-0" />
                <div className="font-display font-bold text-white text-sm">
                  {n}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start space-y-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-sky-400"></span>
              <span className="eyebrow">FAQ — {cfg.name}</span>
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
              {cfg.name} questions, answered.
            </h2>
            <p className="text-zinc-400">
              Don't see your question? Call{" "}
              <a href="tel:+15105079318" className="text-sky-400 hover:underline">
                (510) 507-9318
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            {cfg.faq.map((f, i) => (
              <FAQItem key={i} index={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#09090b] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(14,165,233,0.15)_0%,_transparent_60%)]"></div>
        <div className="relative mx-auto max-w-4xl px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-display font-black text-white text-4xl sm:text-6xl leading-[0.95] tracking-tighter">
            Ready to start your {cfg.name} project?
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            {t.cta.subtitle}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="tel:+15105079318" className="btn-primary">
              <Phone size={16} />
              Call Now · (510) 507-9318
            </a>
            <button onClick={openQuote} className="btn-secondary">
              Request Quote
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* OTHER CITIES */}
      <section className="py-16 bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="eyebrow mb-6">Other locations we serve</div>
          <div className="flex flex-wrap gap-3">
            {Object.values(CITY_PAGES)
              .filter((c) => c.slug !== cfg.slug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/locations/${c.slug}`}
                  data-testid={`related-city-${c.slug}`}
                  className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-sm font-display font-semibold text-white hover:border-sky-400 hover:text-sky-400 transition-colors"
                >
                  <MapPin size={12} />
                  {c.name}
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
      <QuoteFormDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
};

const ServiceTile = ({ idx, service, city, onQuoteClick }) => (
  <div
    data-testid={`city-service-${idx}`}
    className="bg-zinc-950 p-8 hover:bg-sky-500/[0.03] transition-colors group"
  >
    <div className="text-zinc-500 text-xs font-mono mb-3">
      0{idx + 1} / 0{COMMON_SERVICES.length}
    </div>
    <h3 className="font-display font-bold text-white text-xl mb-2 leading-tight">
      {service.short} in {city}
    </h3>
    <p className="text-zinc-400 text-sm leading-relaxed mb-5">
      {service.label} done right — local crew, factory-spec assembly, clean
      finish.
    </p>
    {service.key ? (
      <Link
        to={`/${service.key}`}
        className="inline-flex items-center gap-2 text-sky-400 text-xs font-display font-semibold uppercase tracking-[0.18em] group-hover:gap-3 transition-all"
      >
        Learn more
        <ArrowRight size={14} />
      </Link>
    ) : (
      <button
        onClick={onQuoteClick}
        className="inline-flex items-center gap-2 text-sky-400 text-xs font-display font-semibold uppercase tracking-[0.18em] group-hover:gap-3 transition-all"
      >
        Get Quote
        <ArrowRight size={14} />
      </button>
    )}
  </div>
);

const Trust = ({ Icon, label }) => (
  <div className="flex items-center gap-2 text-zinc-300 text-sm">
    <Icon size={14} className="text-sky-400" />
    <span>{label}</span>
  </div>
);

export default CityPage;
